import { useMemo, useRef, useState } from 'react';
import { useClampOverflow } from '~/ui-rocco/hooks/use-clamp-overflow';
import { Link } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { addUtmToContentLinks } from '~/modules/tracking/util/add-utm-to-content-links';
import {
    linkifyHtml,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import { htmlToPlainText } from '~/ui-rocco/pages/detail-shared/sentence-offsets';
import { useProductDetailPairs } from '../ProductCreditsRow';
import { useProductDetailItems } from '../spec-accordion/use-product-detail-items';
import { ProductSpecList } from '../spec-accordion/ProductSpecList';

/** The body's reading type: the description as running text, no lead. */
const BODY = 'font-sans text-base leading-[1.4] font-normal text-[#323232]';

/**
 * Markup the three clamped lines cannot carry: a link the reader could follow,
 * a picture, a list, a table. Plain text keeps the words and drops all of it,
 * so a short description holding any of these has more to show even when it
 * does not overflow.
 */
const BEYOND_PLAIN_TEXT = /<(?:a\s|img\b|ul\b|ol\b|table\b|iframe\b)/i;

const TOGGLE =
    'mt-2 cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-base leading-[1.4] font-normal text-[#767676]';

/**
 * The description on the phone, with the purchase block: three lines, then
 * Read more for the rest, Product Details and the specs behind it.
 *
 * Set as one run of text. The column this came from lifted the first
 * sentence into a heading, which misfires on the first full stop — "Designed
 * by Peter J." — and gives the supplier's copy a title it never wrote.
 *
 * Collapsed, it is the plain text clamped to three lines, so the cut falls on
 * a line rather than on whatever paragraph the HTML happens to break at.
 * Open, it is the copy as written, links and all, with the product's facts
 * under it.
 */
export const MobileProductDescription = () => {
    const { descriptionHtml, items, scopeOfUseHtml, customSections } =
        useProductDetailItems();
    const hasSpecs =
        items.length > 0 || !!scopeOfUseHtml || customSections.length > 0;
    const pairs = useProductDetailPairs();
    const [expanded, setExpanded] = useState(false);
    const clampRef = useRef<HTMLParagraphElement>(null);

    const html = useMemo(
        () =>
            descriptionHtml
                ? addUtmToContentLinks(linkifyHtml(descriptionHtml))
                : '',
        [descriptionHtml],
    );
    const plainText = useMemo(() => htmlToPlainText(html), [html]);
    const richerThanPlainText = useMemo(
        () => BEYOND_PLAIN_TEXT.test(html),
        [html],
    );
    const overflows = useClampOverflow(clampRef, plainText);

    // A description of nothing but a picture has no plain text, and was read
    // as no description at all.
    if (!plainText && !richerThanPlainText && pairs.length === 0 && !hasSpecs)
        return null;

    // Anything the three lines do not show: the rest of the copy, the links
    // and pictures the clamp flattens away, or the facts that only live
    // behind the toggle.
    const hasMore =
        overflows || richerThanPlainText || pairs.length > 0 || hasSpecs;

    return (
        <div>
            {expanded ? (
                html ? (
                    <div
                        className={cn(BODY, RICH_TEXT_HTML_CLASS)}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                ) : null
            ) : plainText ? (
                <p ref={clampRef} className={cn(BODY, 'm-0 line-clamp-3')}>
                    {plainText}
                </p>
            ) : null}

            {expanded && pairs.length > 0 ? (
                <div className={cn(plainText && 'mt-6')}>
                    <p className="m-0 font-sans text-base leading-[1.4] font-medium text-[#323232]">
                        Product Details
                    </p>
                    <dl className="m-0 mt-2 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-4 gap-y-1.5">
                        {pairs.map((pair, index) => (
                            <div
                                key={`${pair.label}-${index}`}
                                className="contents"
                            >
                                <dt className="font-sans text-sm leading-[1.4] text-[#767676]">
                                    {pair.label}
                                </dt>
                                <dd className="m-0 font-sans text-sm leading-[1.4] break-words text-[#323232]">
                                    {pair.link ? (
                                        <Link
                                            to={pair.link}
                                            className="underline decoration-black/25 underline-offset-2"
                                        >
                                            {pair.value}
                                        </Link>
                                    ) : (
                                        pair.value
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ) : null}

            {expanded && hasSpecs ? (
                // The spec rows as the desktop column sets them — Specs,
                // scope of use, the supplier's own tabs — without the
                // description, which is already above.
                <div className="mt-6">
                    <ProductSpecList
                        items={items}
                        scopeOfUseHtml={scopeOfUseHtml}
                        customSections={customSections}
                    />
                </div>
            ) : null}

            {hasMore ? (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                    className={TOGGLE}
                >
                    {expanded ? 'Read less' : 'Read more'}
                </button>
            ) : null}
        </div>
    );
};
